/**
 * Book Database - Single Source of Truth
 * Add new books here and they'll automatically appear on the site
 */

export interface LevelData {
  id: string
  name: string
  shortName: string
  stars: number
  path: string
  thumbnail: string
  heroImage: string
  alt: string
  isPractice?: boolean
}

export interface BookData {
  title: string
  image: string
  url: string
  description: string
}

/**
 * Reading Levels - Single source of truth
 * Used for: home page cards, routing, and level pages
 */
export const LEVELS_DATA: LevelData[] = [
  {
    id: 'countdown',
    name: 'Countdown to blastoff!',
    shortName: 'Countdown',
    stars: 1,
    path: '/countdown',
    thumbnail: '/images/thumbnails/countdown-hero-thumb.jpg',
    heroImage: '/images/hero/countdown-hero.jpg',
    alt: 'Countdown to Blastoff',
  },
  {
    id: 'blastoff',
    name: 'Blast off!',
    shortName: 'Blast off!',
    stars: 2,
    path: '/blastoff',
    thumbnail: '/images/thumbnails/blastoff-hero-thumb.jpg',
    heroImage: '/images/hero/blastoff-hero.jpg',
    alt: 'Blastoff!',
  },
  {
    id: 'space',
    name: 'Shooting through space',
    shortName: 'Space',
    stars: 3,
    path: '/space',
    thumbnail: '/images/thumbnails/space-hero-thumb.jpg',
    heroImage: '/images/hero/space-hero.jpg',
    alt: 'Shooting Through Space',
  },
  {
    id: 'moon',
    name: 'Landing on the Moon',
    shortName: 'Moon',
    stars: 4,
    path: '/moon',
    thumbnail: '/images/thumbnails/moon-hero-thumb.jpg',
    heroImage: '/images/hero/moon-hero.jpg',
    alt: 'Landing on the Moon',
  },
  {
    id: 'mars',
    name: 'Shoot to Mars',
    shortName: 'Mars',
    stars: 5,
    path: '/mars',
    thumbnail: '/images/thumbnails/mars-hero-thumb.jpg',
    heroImage: '/images/hero/mars-hero.jpg',
    alt: 'Shoot to Mars',
  },
  {
    id: 'practice',
    name: 'Practice Station',
    shortName: 'Practice',
    stars: 0,
    path: '/practice',
    thumbnail: '/images/thumbnails/practice-station.jpg',
    heroImage: '/images/hero/hero-banner.jpg',
    alt: 'Practice Station',
    isPractice: true,
  },
]

/**
 * Books organized by reading level
 */
export const BOOKS_DATA: Record<string, BookData[]> = {
  countdown: [
    {
      title: 'Bathe the Cat',
      image: '/images/books/bathethecat.jpg',
      url: 'https://bookshop.org/p/books/bathe-the-cat-alice-b-mcginty/16458566?ean=9781452142708',
      description: "It's really funny, it makes everyone laugh.",
    },
    {
      title: 'A Crown for Corina',
      image: '/images/books/acrownforcorina.jpg',
      url: 'https://bookshop.org/p/books/a-crown-for-corina-laekan-zea-kemp/18559213?ean=9780759556843',
      description:
        'This book is fun to read, has pretty pictures, the message is pretty cute, some of the words are in Spanish.',
    },
    {
      title: 'I Need a New Butt',
      image: '/images/books/i-need-anew-butt.jpg',
      url: 'https://bookshop.org/p/books/i-need-a-new-butt-dawn-mcmillan/11818422?ean=9780486787992',
      description: 'May be inappropriate for some kids, the last page is sooo funny.',
    },
  ],

  blastoff: [
    {
      title: 'Dog Man',
      image: '/images/books/dogman.jpg',
      url: 'https://bookshop.org/p/books/dog-man-a-graphic-novel-dog-man-1-from-the-creator-of-captain-underpants-1-dav-pilkey/17237096?ean=9781338741032',
      description: "It's very funny, misspelled words, funny illustrations.",
    },
    {
      title: 'Cat Kid Comic Club',
      image: '/images/books/catkid.jpg',
      url: 'https://bookshop.org/p/books/cat-kid-comic-club-a-graphic-novel-cat-kid-comic-club-1-from-the-creator-of-dog-man-dav-pilkey/14514645?ean=9781338712766',
      description: 'very funny, super cute illustrations, weird comics.',
    },
  ],

  space: [
    {
      title: 'Mouse Scouts',
      image: '/images/books/mouse-scouts.jpg',
      url: 'https://bookshop.org/p/books/mouse-scouts-camp-out-sarah-dillard/8700275',
      description:
        "It's very adorable. It has a cute story. They remind me that kindness is always the key. It's like Girl Scouts but with mice.",
    },
    {
      title: 'Purrmaids',
      image: '/images/books/purrmaids.jpg',
      url: 'https://bookshop.org/p/books/purrmaids-1-the-scaredy-cat-sudipta-bardhan-quallen/10205425',
      description: "Teaches me a good lesson, it's very cute, super cute illustrations.",
    },
    {
      title: 'Cutiecorns',
      image: '/images/books/cutiecorns.jpg',
      url: 'https://bookshop.org/p/books/carnival-chaos-cutiecorns-4-volume-4-shannon-penney/14573683',
      description: 'This book is very cute, very magical & very funny.',
    },
    {
      title: 'Cloud Puppy',
      image: '/images/books/cloud-puppy.jpg',
      url: 'https://www.goodreads.com/book/show/199798150-cloud-puppy',
      description: "It's really cute, and the lesson is really meaningful to me.",
    },
  ],

  moon: [
    {
      title: 'Dragon Girls',
      image: '/images/books/dragon-girls.jpg',
      url: 'https://bookshop.org/p/books/stella-the-starlight-dragon-dragon-girls-9-maddy-mara/17896968',
      description:
        '<a href="https://bookshop.org/p/books/stella-the-starlight-dragon-dragon-girls-9-maddy-mara/17896968">Stella the Starlight Dragon</a><br>it\'s very cute, funny illustrations, very magical dragons.',
    },
    {
      title: "Starfish's Story",
      image: '/images/books/starfish.jpg',
      url: 'https://bookshop.org/p/books/starfish-s-story-the-dodo-bonnie-bader/19892515',
      description:
        '<a href="https://bookshop.org/p/books/starfish-s-story-the-dodo-bonnie-bader/19892515">The true story of how one little puppy learned to walk</a><br>It\'s very cute, it is amazing how Starfish learns to walk. I love the way that they set up obstacle courses in their backyard. I love the way that he takes his time, and makes progress, and I love how all of that work leads to amazing results.<br>If you like this book, you should also check out these books:<br><ul><li><a href="https://bookshop.org/p/books/rosie-s-story-the-dodo-bonnie-bader/18340833">Rosie\'s Story</a> - The story of a cat who is befriended by a husky named Lilo.</li><li><a href="https://bookshop.org/p/books/zeus-water-rescue-dogs-with-a-purpose/18860046">Zeus: Water Rescue</a> - This is the story of a dog named Zeus, who learns how to do water rescue.</li></ul>',
    },
  ],

  mars: [
    {
      title: 'Harry Potter',
      image: '/images/books/harry-potter.jpg',
      url: 'https://bookshop.org/p/books/harry-potter-and-the-sorcerer-s-stone-the-illustrated-edition-illustrated-the-illustrated-edition-volume-1-j-k-rowling/18834259',
      description:
        '<a href="https://bookshop.org/p/books/harry-potter-and-the-sorcerer-s-stone-the-illustrated-edition-illustrated-the-illustrated-edition-volume-1-j-k-rowling/18834259">and the Sorcerer\'s Stone</a><br>this book has a lot of magic, it\'s very long & cool.',
    },
    {
      title: 'Emily Windsnap',
      image: '/images/books/windsnap.jpg',
      url: 'https://bookshop.org/p/books/emily-windsnap-and-the-siren-s-secret-liz-kessler/11174151',
      description:
        '<a href="https://bookshop.org/p/books/emily-windsnap-and-the-siren-s-secret-liz-kessler/11174151">and The Siren\'s Secret</a><br>it\'s very good, super action-packed, very amazing.',
    },
    {
      title: 'Winterhouse',
      image: '/images/books/9781250294197.jpg',
      url: 'https://bookshop.org/p/books/winterhouse-ben-guterson/6986028',
      description: 'It is full of puzzles and codes,',
    },
    {
      title: 'The Unicorn Quest',
      image: '/images/books/unicorn-quest.jpg',
      url: 'https://bookshop.org/p/books/the-unicorn-quest-kamilla-benko/6392584',
      description:
        'It is a really good book. I love the story, the fact that they are questing for unicorns, the way Claire makes friends, and her love for her sister grows throughout the book as she searches for her.<br>I recommend the entire series<br><ul><li><a href="https://bookshop.org/p/books/secret-in-the-stone-kamilla-benko/379262">The Unicorn Quest - Secret in the Stone</a></li><li><a href="https://bookshop.org/p/books/fire-in-the-star-kamilla-benko/14669714">The Unicorn Quest - Fire in the Star</a><br>and another book set in the same world</li><li><a href="https://bookshop.org/p/books/the-unicorn-legacy-tangled-magic-kamilla-benko/18498165">The Unicorn Legacy - Tangled Magic</a></li></ul>',
    },
  ],
}
